import { useEffect, useRef, useState } from 'react'
import { reportPictures, type FilterLibrary } from '../services/autoFilterService'
import './report.css'

/**
 * Report: "these pictures' tags are wrong" (Huanrui, 2026-10-05)
 * -------------------------------------------------------------
 * A small red ! beside the selection's actions -- after Copy to project on
 * the Audit page, after Add to board in the Miro panel. The first click asks
 * ("Report 3?"), the second sends; nothing happens on one click alone, and
 * the question goes away by itself after a few seconds. The pictures are
 * tagged again by Auto Filter in the background (routes/autoFilter.ts,
 * reportPictures).
 */
export function ReportButton({
  assetIds,
  size = 'normal',
  library = 'audit',
  groups,
}: {
  assetIds: string[]
  size?: 'normal' | 'small'
  /** Which library's tags are wrong: Audit or Design Works (2026-10-08). */
  library?: FilterLibrary
  /**
   * Pictures from several libraries at once (Internal Library, 2026-10-14):
   * each group is reported to its own library, and `assetIds` is ignored.
   */
  groups?: { library: FilterLibrary; ids: string[] }[]
}) {
  const [state, setState] = useState<'idle' | 'asking' | 'sending' | 'done' | 'failed'>('idle')
  const [note, setNote] = useState('')
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => () => window.clearTimeout(timer.current), [])
  const after = (ms: number, next: typeof state) => {
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setState(next), ms)
  }

  const sending = groups ?? [{ library, ids: assetIds }]
  const count = sending.reduce((sum, one) => sum + one.ids.length, 0)
  const label =
    state === 'asking'
      ? `Report ${count}?`
      : state === 'sending'
        ? 'Reporting…'
        : state === 'done'
          ? 'Reported'
          : state === 'failed'
            ? 'Not sent'
            : ''

  return (
    <button
      type="button"
      className={`ww-report ww-report--${size}${state !== 'idle' ? ' ww-report--open' : ''}${state === 'done' ? ' ww-report--done' : ''}`}
      disabled={count === 0 || state === 'sending'}
      title={state === 'failed' ? note : `Report wrong tags on ${count} picture${count === 1 ? '' : 's'}: Auto Filter tags them again`}
      aria-label={label || 'Report wrong tags'}
      onClick={() => {
        if (state === 'idle' || state === 'failed' || state === 'done') {
          setState('asking')
          after(4000, 'idle')
          return
        }
        if (state !== 'asking') return
        window.clearTimeout(timer.current)
        setState('sending')
        Promise.all(sending.filter((one) => one.ids.length > 0).map((one) => reportPictures(one.ids, one.library)))
          .then(() => {
            setState('done')
            after(2500, 'idle')
          })
          .catch((err: unknown) => {
            setNote(err instanceof Error ? err.message : 'Could not report')
            setState('failed')
            after(4000, 'idle')
          })
      }}
    >
      <span className="ww-report__mark" aria-hidden="true">
        {state === 'done' ? '✓' : '!'}
      </span>
      {label ? <span className="ww-report__text">{label}</span> : null}
    </button>
  )
}
